import { DEEP_LINK_GPU_COOKIE_KEY, DEEP_LINK_LOCATION_COOKIE_KEY } from "@/constants/cluster";
import {
  ATTRIBUTION_STORAGE_KEY,
  getAmplitude,
  SESSION_UTM_KEY,
  UTM_KEYS
} from "@/utils/amplitude";
import localCookies from "@/utils/cookie";
import { useEffect, useMemo } from "react";
import { useLocation, useSearch } from "wouter";

/**
 * This component handles all anonymous user tracking for the Web App.
 * NOTE: Auto-capture for page views MUST be disabled in the Amplitude init config.
 */
export const ClientAnalyticsTracker = ({ isInitialised }: { isInitialised: boolean }) => {
  const queryParams = useSearch();
  const [location] = useLocation();

  // Signature of only UTM parameters in the query string, so the effect
  // below doesn't re-run for unrelated query param changes.
  const utmSignature = useMemo(() => {
    const searchParams = new URLSearchParams(queryParams);
    const parts: string[] = [];
    for (const key of UTM_KEYS) {
      if (searchParams.has(key)) {
        parts.push(`${key}=${searchParams.get(key) as string}`);
      }
    }
    return parts.join("&");
  }, [queryParams]);

  // HOOK 1: Capture First-Touch data ONCE on the initial visit.
  useEffect(() => {
    // On first load, we can still use window.location.search
    const initialSearchParams = new URLSearchParams(window.location.search);

    if (
      (!localCookies.getItem(ATTRIBUTION_STORAGE_KEY) &&
        (initialSearchParams.has("utm_source") || initialSearchParams.has("gclid"))) ||
      initialSearchParams.has("fbclid") ||
      initialSearchParams.has("gbraid") ||
      initialSearchParams.has("wbraid") ||
      initialSearchParams.has("li_fat_id") ||
      initialSearchParams.has("twclid")
    ) {
      const attributionData: Record<string, string> = {
        initial_referrer: document.referrer || "direct"
      };
      for (const key of UTM_KEYS) {
        if (initialSearchParams.has(key)) {
          attributionData[`initial_${key}`] = initialSearchParams.get(key) as string;
        }
      }
      localCookies.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attributionData));
    }

    // Check if session UTMs have already been stored.
    const hasSessionUtms = localCookies.getItem(SESSION_UTM_KEY);
    // If not stored yet AND UTMs are in the current URL, store them.
    if (
      (!hasSessionUtms &&
        (initialSearchParams.has("utm_source") || initialSearchParams.has("gclid"))) ||
      initialSearchParams.has("fbclid") ||
      initialSearchParams.has("gbraid") ||
      initialSearchParams.has("wbraid") ||
      initialSearchParams.has("li_fat_id") ||
      initialSearchParams.has("twclid")
    ) {
      const sessionUtms: Record<string, string> = {};
      for (const key of UTM_KEYS) {
        if (initialSearchParams.has(key)) {
          sessionUtms[key] = initialSearchParams.get(key) as string;
        }
      }
      localCookies.setItem(SESSION_UTM_KEY, JSON.stringify(sessionUtms), true);
    }

    // Unrelated to the analytics, this logic is to record gpu name from marketing link
    if (initialSearchParams.has("gpu")) {
      localCookies.setItem(DEEP_LINK_GPU_COOKIE_KEY, initialSearchParams.get("gpu") as string);
    }
    if (initialSearchParams.has("location")) {
      localCookies.setItem(
        DEEP_LINK_LOCATION_COOKIE_KEY,
        initialSearchParams.get("location") as string
      );
    }
  }, []); // Empty dependency array ensures this runs only once.

  // HOOK 2: Track a "Page View" on every client-side navigation.
  useEffect(() => {
    if (!isInitialised) return;

    const amplitude = getAmplitude();
    if (!amplitude) return;
    console.log("ClientAnalyticsTracker - amplitude");

    const eventProperties: Record<string, string> = { page_path: location };
    if (utmSignature) {
      for (const pair of utmSignature.split("&")) {
        const [key, value] = pair.split("=");
        if (key && value) {
          eventProperties[key] = value;
        }
      }
    }
    amplitude.logEvent("Page View - Enriched", eventProperties);
  }, [isInitialised, location, utmSignature]); // Re-run on navigation or when UTM params change.

  return null;
};
